import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageButtons from 'components/navbar/top/languageBtn';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from "uuid";
import { getDomainConfig } from "../../config/domainConfig";

const getSuggestedPlanType = (plan, hasNoActivePlan) => {
    if (hasNoActivePlan) return "basic";
    if (plan === "Basic") return "standard";
    if (plan === "Standard") return "basic";
    return "basic";
};

const SubscriptionRenewal = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [selectedPlanType, setSelectedPlanType] = useState('basic');
  const [userCount, setUserCount] = useState(1);
  const [planPrices, setPlanPrices] = useState({});
  const domainConfig = getDomainConfig();

  useEffect(() => {
    const fetchPlanPrices = async () => {
      try {

        const provider = domainConfig.gatewayName.toUpperCase();

        const pricesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/subscriptionProviderConfigRoute/provider/${provider}`,
          {
            headers: {
              Authorization:
                process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        const priceMap = {};

        (pricesRes.data || []).forEach(item => {
          priceMap[item.subscriptionType] = item;
        });

        setPlanPrices(priceMap);

      } catch (error) {

        console.error(
          'Error fetching plan prices:',
          error
        );
      }
    };

    fetchPlanPrices();

  }, []);

  const planConfig = {
    basic: {
      price: planPrices['Basic']?.amount,
      currency: planPrices['Basic']?.currency,
      users: planPrices['Basic']?.maxUsers,
      label: t('subscription.plan.basic')
    },

    standard: {
      price: planPrices['Standard']?.amount,
      currency: planPrices['Standard']?.currency,
      users: planPrices['Standard']?.maxUsers,
      label: t('subscription.plan.standard')
    }
  };

  const currency = planConfig[selectedPlanType]?.currency
  const isRazorpay = domainConfig.gatewayName === "Razorpay";
  const paymentRoute = isRazorpay? 'razorpayRoute': 'mollieRoute';
  const currentPlan = customerData?.Subscription?.SubscriptionType;

  const isSubscriptionExpired =
    customerData?.ProviderSubscriptionStatus === "Cancelled" &&
    customerData?.SubscriptionEndDate &&
    new Date(customerData.SubscriptionEndDate) < new Date();

  const isOffboardOrDisabled =
    customerData?.IsActive === "Offboard" || customerData?.IsActive === "Disable";

  const paymentRequired = !!customerData?.PaymentRequired;

  const hasNoActivePlan = isSubscriptionExpired || isOffboardOrDisabled || paymentRequired;

  const isTrial = currentPlan === "Trial" || hasNoActivePlan;
  const isBasic = currentPlan === "Basic" && !hasNoActivePlan;
  const isStandard = currentPlan === "Standard" && !hasNoActivePlan;

  const hasActiveMollieSubscription = customerData?.MollieSubscriptionStatus?.toLowerCase() === "active";

  const isUpgrade = isBasic || isStandard || (isTrial && hasActiveMollieSubscription);

  const previousSubscriptionType = currentPlan || null;

  useEffect(() => {
    setSelectedPlanType(getSuggestedPlanType(currentPlan, hasNoActivePlan));
  }, [currentPlan, hasNoActivePlan]);

  useEffect(() => {
    if (planConfig[selectedPlanType]) {
      setUserCount(planConfig[selectedPlanType].users);
    }
  }, [selectedPlanType, planPrices]);

  const getActivationDate = () => new Date();

  const getNextBillingDate = () => {
    const next = new Date();
    next.setMonth(next.getMonth() + 1);
    return next;
  };

  useEffect(() => {
    sessionStorage.setItem("selectedPlan", selectedPlan);
    sessionStorage.setItem("userCount", userCount);
  }, [selectedPlan, userCount]);

  useEffect(() => {
    const savedPlan = sessionStorage.getItem("selectedPlan");
    if (savedPlan) setSelectedPlan(savedPlan);
  }, []);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        if (!customerId) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        setCustomerData(response.data);
        const fetchedPlan = response.data?.Subscription?.SubscriptionType;

        const fetchedIsExpired =
          response.data?.ProviderSubscriptionStatus === "Cancelled" &&
          response.data?.SubscriptionEndDate &&
          new Date(response.data.SubscriptionEndDate) < new Date();

        const fetchedIsOffboardOrDisabled =
          response.data?.IsActive === "Offboard" || response.data?.IsActive === "Disable";

        const fetchedPaymentRequired = !!response.data?.PaymentRequired;

        const fetchedHasNoActivePlan = fetchedIsExpired || fetchedIsOffboardOrDisabled || fetchedPaymentRequired;

        setSelectedPlanType(getSuggestedPlanType(fetchedPlan, fetchedHasNoActivePlan));
      } catch (error) {
        console.error("Error fetching customer data:", error);
      }
    };
    fetchCustomerData();
  }, []);

  const calculateTotal = () => planConfig[selectedPlanType].price;

  const generateSessionId = () => {
    const sessionId = uuidv4();
    sessionStorage.setItem("sessionId", sessionId);
    return sessionId;
  };

  const ensureProviderCustomer = async (customerData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/${paymentRoute}/customer/ensureProviderCustomer`,
        { customer: customerData },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      return response.data?.ProviderCustomerId || response.data?.MollieCustomerId;
    } catch (error) {
      console.error('ensureProviderCustomer failed:', error);
      return null;
    }
  };

  const createCustomerPayment = async (providerCustomerId, paymentBody) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/${paymentRoute}/customer/createCustomerPayment`,
        { providerCustomerId, paymentBody },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      return response.data;
    } catch (error) {
      console.error('createCustomerPayment failed:', error);
      return null;
    }
  };

  const saveSessionData = (sessionData) =>
    axios.post(
      `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/sessionDataRoute/create`,
      sessionData,
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    ).catch(err => console.error("Session save failed", err));

  const selectedPlanConfig =
    selectedPlanType === "basic"
      ? planPrices["Basic"]
      : planPrices["Standard"];

  const initiateCustomerPayment = async () => {
    setLoading(true);

    try {
      if (!customerData) {
        toast.error(t("customerDataMissing"));
        setLoading(false);
        return;
      }

      const sessionId = generateSessionId();
      const culture = localStorage.getItem("culture");
      const totalAmount = calculateTotal().toFixed(2);

      const providerCustomerId = await ensureProviderCustomer(customerData);

      if (!providerCustomerId) {
        setLoading(false);
        return;
      }

      const paymentBody = {
        amount: {
          currency: currency,
          value: totalAmount
        },
        description: t("initialSubscription", {
          subscriptionType: planConfig[selectedPlanType].label,
          noOfUsers: userCount,
        }),
        redirectUrl: `${process.env.REACT_APP_BASE_URL}/payment/result/${sessionId}?culture=${culture}`,
        cancelUrl: `${process.env.REACT_APP_BASE_URL}/dashboard?culture=${culture}`,
        webhookUrl:process.env.MOLLIE_WEBHOOK_URL,
        sequenceType: "first",
        locale: culture === "nl" ? "nl_NL" : "en_US",
        metadata: {
          sessionId,
          localCustomerId: customerData.CustomerId,
          noOfUsers: userCount,
          createSubscription: false,

          amount: totalAmount,
          currency: currency,

          culture,
          interval: selectedPlanConfig?.billingInterval === 'monthly'
            ? '1 month'
            : selectedPlanConfig?.billingInterval,
          subscriptionType: selectedPlanType,
          customerName: customerData.CustomerName,
          customerEmail: customerData.Email,
          isUpgrade,
          previousSubscriptionType,
        },
      };

      const payment = await createCustomerPayment(
        providerCustomerId,
        paymentBody
      );

      if (!payment) {
        setLoading(false);
        return;
      }

      await saveSessionData({
        SessionId: sessionId,
        CustomerId: customerData.CustomerId,
        Language: culture,
        Currency: currency,
        Amount: totalAmount,
        PaymentId: payment.molliePaymentId || payment.subscriptionId,
        PaymentStatus: "open",
        IsWebhookProcessed: false,
        MoolieCustomerId: providerCustomerId,
        SubscriptionType: selectedPlanType,
        NoOfUsers: userCount,
        ProviderSubscriptionId: payment.subscriptionId,
        ProviderName: domainConfig.gatewayName.toUpperCase(),
      });

      // =========================
      // Razorpay Flow
      // =========================
      if (isRazorpay) {

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";

        script.onload = () => {

          const options = {
            key: payment.key || process.env.REACT_APP_RAZORPAY_KEY_ID,

            subscription_id: payment.subscriptionId,

            name:
              customerData.OrganisationName ||
              customerData.CustomerName,

            description: t("initialSubscription", {
              subscriptionType:
                planConfig[selectedPlanType].label,
              noOfUsers: userCount,
            }),

            prefill: {
              name: customerData.CustomerName,
              email: customerData.Email,
              contact: customerData.PhoneNumber || "",
            },

            handler: async (response) => {
              try {

                await axios.post(
                  `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/${paymentRoute}/verify`,
                  {
                    razorpay_payment_id:
                      response.razorpay_payment_id,

                    razorpay_subscription_id:
                      response.razorpay_subscription_id,

                    razorpay_signature:
                      response.razorpay_signature,

                    sessionId,
                  },
                  {
                    headers: {
                      Authorization:
                        process.env.REACT_APP_BasicAuthSecret,
                    },
                  }
                );

                window.location.href =
                  `${process.env.REACT_APP_BASE_URL}/payment/result/${sessionId}?culture=${culture}`;

              } catch (err) {

                console.error(
                  "Razorpay payment verification failed:",
                  err
                );

                toast.error(
                  t("paymentVerificationFailed")
                );

                setLoading(false);
              }
            },

            modal: {
              ondismiss: () => setLoading(false),
            },
          };

          new window.Razorpay(options).open();

          setLoading(false);
        };

        document.body.appendChild(script);

      }else {

        if (payment.checkoutUrl) {

          setLoading(false);

          window.location.href = payment.checkoutUrl;

          return;
        }
      }

      setLoading(false);

    } catch (err) {

      console.error(
        "initiateCustomerPayment error:",
        err
      );

      toast.error(t("paymentInitFailed"));

      setLoading(false);
    }
  };

  const formatEUDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const mon = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}-${mon}-${d.getFullYear()}`;
  };

 const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 2
  }).format(price || 0);

  if (!customerData) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="language-buttons">
        <LanguageButtons />
      </div>

      <div className="container-fluid subscription-renewal-container">

        {/* Header */}
        <div className="text-center">
          <h2 className="fw-bold text-primary mt-3 mb-4">
            {t('subscription.renew.title')}
          </h2>
          {/* <p className="text-secondary mw-500 mx-auto">
            {t('subscription.renew.description')}
          </p> */}
        </div>

        <Row className="align-items-stretch g-3">

          {/* ── LEFT: Customer + Plan ── */}
          <Col lg={7}>
            <Card className="subscription-card h-100">
              <div className="card-header-primary">
                <h6 className="fw-semibold text-white mb-0 text-center">
                  {t('subscription.customer.infoTitle') || 'Customer Information'}
                </h6>
              </div>

              <Card.Body className="p-4">

                {/* Customer info */}
                <h6 className="section-title text-primary fw-semibold mb-2">
                  {t('subscription.customer.information')}
                </h6>
                <div className="mb-3">
                  {[
                    [t('subscription.customer.name'), customerData.CustomerName],
                    [t('subscription.customer.email'), customerData.Email],
                    [t('subscription.customer.phone'), customerData.PhoneNumber],
                    [t('subscription.business.organisationName'), customerData.OrganisationName],
                  ].map(([label, value]) => (
                    <div key={label} className="d-flex justify-content-between align-items-center py-2">
                      <small className="text-secondary">{label}</small>
                      <small className="fw-semibold text-end text-break">{value || 'N/A'}</small>
                    </div>
                  ))}
                </div>

                {/* Subscription */}
                <h6 className="section-title text-primary fw-semibold mb-2">
                  {t('subscription.customer.subscription')}
                </h6>
                <div className="mb-3">

                  {/* Plan selector */}
                  <div className="subscription-plan-row d-flex justify-content-between align-items-end py-2">
                    <small className="text-secondary">{t('subscription.customer.plan')}</small>
                    <div className="d-flex align-items-center gap-2 ms-auto">
                      {isTrial && (
                        <div className="plan-toggle-wrapper d-inline-flex align-items-center rounded-pill p-1 bg-custom-light border">
                      <button
                        type="button"
                        onClick={() => setSelectedPlanType('basic')}
                        className={`border-0 rounded-pill px-2 py-0 fw-medium font-size-13 h-100 ${
                          selectedPlanType === 'basic'
                            ? 'toggle-btn-selected'
                            : 'bg-transparent text-secondary'
                        }`}
                      >
                        {t('subscription.plan.basic')}
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedPlanType('standard')}
                        className={`border-0 rounded-pill px-2 py-0 fw-medium font-size-13 h-100 ${
                          selectedPlanType === 'standard'
                            ? 'toggle-btn-selected'
                            : 'bg-transparent text-secondary'
                        }`}
                      >
                        {t('subscription.plan.standard')}
                      </button>
                    </div>
                      )}
                      {isBasic && (
                        <small className="fw-semibold text-end">
                          {t('subscription.plan.standard')}
                        </small>
                      )}
                      {isStandard && (
                        <small className="fw-semibold text-end">
                          {t('subscription.plan.basic')}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <small className="text-secondary">{t('subscription.customer.activation')}</small>
                    <small className="fw-semibold text-end">{formatEUDate(getActivationDate())}</small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <small className="text-secondary">{t('subscription.customer.nextBilling')}</small>
                    <small className="fw-semibold text-end">{formatEUDate(getNextBillingDate())}</small>
                  </div>
                </div>

                {/* Team size */}
                <h6 className="section-title text-primary fw-semibold mb-3">
                  {t('subscription.team.size')}
                </h6>
                <div className="user-count-box p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="user-icon"><i className="fas fa-users"></i></div>
                      <div className="ms-3">
                        <h6 className="mb-0 fw-semibold">{t('subscription.user.count')}</h6>
                      </div>
                    </div>
                    <div className="user-counter d-flex align-items-center">
                      <div className="user-number">{userCount}</div>
                    </div>
                  </div>
                </div>

              </Card.Body>
            </Card>
          </Col>

          {/* ── RIGHT: Billing cycle + Summary ── */}
          <Col lg={5} className="d-flex flex-column gap-3">
            <Card className="subscription-card">
              <div className="card-header-primary">
                <h6 className="fw-semibold text-white mb-0 text-center">
                  {t('subscription.billing.cycle')}
                </h6>
              </div>
              <Card.Body className="p-4">
                <div className="billing-box p-3 border rounded-3 bg-light">
                  {/* <Form.Check type="radio" name="billingCycle"
                    checked={selectedPlan === 'monthly'}
                    onChange={() => setSelectedPlan('monthly')}
                    label={t('subscription.billing.monthly')}
                    className="mb-1 fw-semibold" /> */}
                  <span className="fw-semibold fs-10">
                      {t('monthlySubscription')}
                  </span>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="total-amount fw-bold text-primary">
                      {formatPrice(planConfig[selectedPlanType].price)}
                    </span>
                    {/* <small className="text-secondary">{t('perMonth')}</small> */}
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="subscription-card flex-grow-1">
              <div className="card-header-primary">
                <h6 className="fw-semibold text-white mb-0 text-center">
                  {t('subscription.order.summary')}
                </h6>
              </div>
              <Card.Body className="p-4">
                <div className="mb-2">
                  <span className="text-primary fw-semibold">{t('subscription.starter.plan')}</span>
                  <div className="d-flex flex-column gap-2 mt-3">
                    <div className="d-flex justify-content-between">
                      <small className="text-secondary">{t('subscription.subtotal')}:</small>
                      <small className="fw-semibold">{formatPrice(calculateTotal())}</small>
                    </div>
                    <div className="d-flex justify-content-between">
                      <small className="text-secondary">{t('subscription.tax')}:</small>
                      <small className="fw-semibold">{formatPrice(0)}</small>
                    </div>
                  </div>
                </div>

                <div className="border-top pt-3 mb-2">
                  <div className="d-flex justify-content-between">
                    <span className="total-label fw-bold text-primary">{t('subscription.total')}:</span>
                    <span className="total-amount fw-bold text-primary">
                      {formatPrice(calculateTotal())}
                    </span>
                  </div>
                </div>

                <Button
                  variant="primary"
                  className="w-100 py-2 border-0 rounded-3 fw-semibold"
                  onClick={initiateCustomerPayment}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {t('subscription.processing')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-lock me-2"></i>
                      {t('subscription.pay', { amount: formatPrice(calculateTotal()) })}
                    </>
                  )}
                </Button>

                <div className="text-center mt-2">
                  <small className="text-secondary">
                    <i className="fas fa-shield-alt me-1"></i>
                     {t('subscription.secure.paymentMessage', { gateway: getDomainConfig().gatewayName })}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SubscriptionRenewal;